import React, { useState, useEffect, useCallback } from 'react';
import {
  Container, Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TablePagination, Button, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, IconButton, Select, MenuItem, FormControl, InputLabel,
  Alert, Tooltip, Chip, InputAdornment, FormGroup, FormControlLabel, Checkbox,
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon, Search as SearchIcon } from '@mui/icons-material';
import axios from 'axios';
import SidebarLayout from '../components/SidebarLayout';

const ALL_PERMS = [
  { key: 'bili_barrage', label: '弹幕' },
  { key: 'bili_comment', label: '评论' },
  { key: 'bili_play', label: '视频播放量' },
] as const;

interface User {
  id: number;
  username: string;
  email: string;
  role: string;
  permissions?: string[];
  created_at?: string;
  createdAt?: string;
  last_comment_time?: string;
  last_barrage_time?: string;
  statVideoId?: number | null;
  statVideoUrl?: string;
}

const defaultPerms = ['bili_barrage', 'bili_comment'];

const UserManagement = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [open, setOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    role: 'user',
    permissions: [...defaultPerms] as string[],
    statVideoUrl: '',
  });
  const [error, setError] = useState('');

  const fetchUsers = useCallback(async () => {
    try {
      const response = await axios.get('/api/users', {
        params: { page: page + 1, limit: rowsPerPage, search: search || undefined },
      });
      setUsers(response.data.users || []);
      setTotal(response.data.total || 0);
    } catch (err) {
      console.error('获取用户列表失败:', err);
    }
  }, [page, rowsPerPage, search]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const handleSearch = () => { setSearch(searchInput); setPage(0); };

  const handleOpen = (user?: User) => {
    setError('');
    if (user) {
      setEditingUser(user);
      setFormData({
        username: user.username,
        email: user.email,
        password: '',
        role: user.role,
        permissions: user.permissions || [...defaultPerms],
        statVideoUrl: user.statVideoUrl || '',
      });
    } else {
      setEditingUser(null);
      setFormData({ username: '', email: '', password: '', role: 'user', permissions: [...defaultPerms], statVideoUrl: '' });
    }
    setOpen(true);
  };

  const handleClose = () => { setOpen(false); setEditingUser(null); };

  const togglePerm = (key: string) => {
    setFormData((prev) => {
      const has = prev.permissions.includes(key);
      return {
        ...prev,
        permissions: has ? prev.permissions.filter((p) => p !== key) : [...prev.permissions, key],
      };
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    try {
      const payload: Record<string, unknown> = {
        username: formData.username,
        email: formData.email,
        role: formData.role,
        permissions: formData.permissions,
        statVideoUrl: formData.statVideoUrl,
      };
      if (formData.password) payload.password = formData.password;

      if (editingUser) {
        await axios.put(`/api/users/${editingUser.id}`, payload);
      } else {
        if (!formData.password) { setError('新用户必须填写密码'); return; }
        await axios.post('/api/users', payload);
      }
      handleClose();
      fetchUsers();
    } catch (err: any) {
      setError(err.response?.data?.message || '保存用户失败');
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('确定要删除这个用户吗？')) return;
    try {
      await axios.delete(`/api/users/${id}`);
      fetchUsers();
    } catch (err: any) {
      setError(err.response?.data?.message || '删除用户失败');
    }
  };

  const isOnline = (timeStr?: string) => {
    if (!timeStr) return false;
    return (Date.now() - new Date(timeStr).getTime()) <= 15 * 60 * 1000;
  };

  const formatTime = (timeStr?: string) => {
    if (!timeStr) return '-';
    const d = new Date(timeStr);
    if (isNaN(d.getTime())) return '-';
    return d.toLocaleString('zh-CN', {
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
    });
  };

  const permLabel = (perms?: string[]) => {
    const list = perms || defaultPerms;
    return ALL_PERMS.filter((p) => list.includes(p.key)).map((p) => p.label).join('、') || '无';
  };

  return (
    <SidebarLayout>
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h4">用户管理</Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField
              size="small" placeholder="搜索用户名" value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={handleSearch}><SearchIcon fontSize="small" /></IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Button variant="contained" onClick={() => handleOpen()}>添加用户</Button>
          </Box>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>用户名</TableCell>
                <TableCell>邮箱</TableCell>
                <TableCell>角色</TableCell>
                <TableCell>功能权限</TableCell>
                <TableCell>统计小视频</TableCell>
                <TableCell>创建时间</TableCell>
                <TableCell>B站评论</TableCell>
                <TableCell>B站弹幕</TableCell>
                <TableCell>操作</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.map((user) => (
                <TableRow key={user.id} hover>
                  <TableCell>{user.username}</TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>
                    <Chip label={user.role === 'admin' ? '管理员' : '普通用户'} size="small"
                      color={user.role === 'admin' ? 'primary' : 'default'} />
                  </TableCell>
                  <TableCell sx={{ maxWidth: 180 }}>{permLabel(user.permissions)}</TableCell>
                  <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                    title={user.statVideoUrl || ''}>
                    {user.statVideoId ? `#${user.statVideoId} ` : ''}{user.statVideoUrl || '-'}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatTime(user.created_at || user.createdAt)}</TableCell>
                  <TableCell>
                    <Tooltip title={`最后活跃：${formatTime(user.last_comment_time)}`} arrow>
                      <Typography variant="body2" color={isOnline(user.last_comment_time) ? 'success.main' : 'text.secondary'}>
                        {isOnline(user.last_comment_time) ? '在线' : '离线'}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <Tooltip title={`最后活跃：${formatTime(user.last_barrage_time)}`} arrow>
                      <Typography variant="body2" color={isOnline(user.last_barrage_time) ? 'success.main' : 'text.secondary'}>
                        {isOnline(user.last_barrage_time) ? '在线' : '离线'}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <IconButton size="small" onClick={() => handleOpen(user)}><EditIcon fontSize="small" /></IconButton>
                    <IconButton size="small" onClick={() => handleDelete(user.id)}><DeleteIcon fontSize="small" /></IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {users.length === 0 && (
                <TableRow><TableCell colSpan={9} align="center">暂无数据</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
          <TablePagination
            component="div" count={total} page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            rowsPerPageOptions={[10, 20, 50]}
            labelRowsPerPage="每页行数"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} / ${count}`}
          />
        </TableContainer>

        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
          <DialogTitle>{editingUser ? '编辑用户' : '添加用户'}</DialogTitle>
          <DialogContent>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
              <TextField fullWidth label="用户名" value={formData.username}
                onChange={(e) => setFormData({ ...formData, username: e.target.value })} margin="normal" required />
              <TextField fullWidth label="邮箱" type="email" value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })} margin="normal" required />
              <TextField fullWidth label={editingUser ? '新密码（不修改留空）' : '密码'} type="password" value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })} margin="normal" required={!editingUser} />
              <FormControl fullWidth margin="normal">
                <InputLabel>角色</InputLabel>
                <Select value={formData.role} label="角色" onChange={(e) => setFormData({ ...formData, role: e.target.value })}>
                  <MenuItem value="user">普通用户</MenuItem>
                  <MenuItem value="admin">管理员</MenuItem>
                </Select>
              </FormControl>
              <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>功能权限</Typography>
              <FormGroup row>
                {ALL_PERMS.map((p) => (
                  <FormControlLabel
                    key={p.key}
                    control={
                      <Checkbox
                        checked={formData.permissions.includes(p.key)}
                        onChange={() => togglePerm(p.key)}
                      />
                    }
                    label={p.label}
                  />
                ))}
              </FormGroup>
              <Typography variant="caption" color="text.secondary">
                默认拥有弹幕和评论权限（兼容老用户）；视频播放量需单独勾选。
              </Typography>
              <TextField fullWidth label="统计小视频地址" value={formData.statVideoUrl}
                onChange={(e) => setFormData({ ...formData, statVideoUrl: e.target.value })}
                margin="normal"
                placeholder="完整链接或 BV 短号"
                helperText="边端刷播必填；支持 BV 短号，一人一条并分配小视频 ID" />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose}>取消</Button>
            <Button onClick={handleSubmit} variant="contained">保存</Button>
          </DialogActions>
        </Dialog>
      </Container>
    </SidebarLayout>
  );
};

export default UserManagement;
